import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { userManager } from './userManager';
import { replaceWith } from '@utils/navigation';
import Loading from '@components/common/Loading';
import Error from '@components/common/Error';

const FAIL_COUNT_KEY = 'oidc_cb_fail_count';
const MAX_CALLBACK_FAILURES = 2;

const AuthCallback = () => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const state = params.get('state');

      if (!params.has('code') || !state) {
        hasProcessed.current = true;
        replaceWith('/');
        return;
      }

      const storageKey = `oidc_cb:${state}`;
      if (sessionStorage.getItem(storageKey)) {
        return;
      }
      sessionStorage.setItem(storageKey, '1');

      try {
        const user = await userManager.signinRedirectCallback();
        sessionStorage.removeItem(FAIL_COUNT_KEY);
        const returnTo =
          (user.state as { returnTo?: string } | undefined)?.returnTo || '/';
        hasProcessed.current = true;
        replaceWith(returnTo);
      } catch (err) {
        sessionStorage.removeItem(storageKey);
        console.error('AuthCallback: Error processing callback:', err);

        const failures =
          Number(sessionStorage.getItem(FAIL_COUNT_KEY) || '0') + 1;
        sessionStorage.setItem(FAIL_COUNT_KEY, String(failures));
        hasProcessed.current = true;

        // Stop bouncing back to login to avoid an endless redirect loop.
        if (failures >= MAX_CALLBACK_FAILURES) {
          setFailed(true);
          return;
        }
        replaceWith('/');
      }
    };

    handleCallback();
  }, []);

  if (failed) {
    return (
      <Error
        title={t('auth.signInFailedTitle')}
        message={t('auth.signInFailedMessage')}
      />
    );
  }

  return <Loading message={t('loading.authentication')} />;
};

export default AuthCallback;
